interface TeamMapData {
  name: string;
  id: number;
  live_children: number;
  archived_children: number;
  programId?: number;
  waveId?: number;
}

interface WaveMapData {
  name: string;
  id: number;
  live_children: number;
  archived_children: number;
  teams: TeamMapData[];
  session_id: number;
  programId?: number;
}

interface ProgramMapData {
  name: string;
  id: number;
  live_children: number;
  archived_children: number;
  teams: TeamMapData[];
  waves: WaveMapData[];
}

interface JourneyMapData {
  name: string;
  id: number;
}

interface ResourceMapData {
  name: string;
  id: number;
}

interface SessionMapData {
  name: string;
  id: number;
  program_id: number;
  wave_id: number;
}

interface ChannelMapData {
  name: string;
  id: number;
}

export interface EditionMapData {
  name: string;
  id: number;
  programs: ProgramMapData[];
  journeys: JourneyMapData[];
  resources: ResourceMapData[];
  sessions: SessionMapData[];
  channels: ChannelMapData[];
  live_children: number;
  archived_children: number;
}

class EditionForMap {
  public name: string;
  public id: number;
  private _programIds: number[];
  private _journeyIds: number[];
  private _resourceIds: number[];
  private _sessionIds: number[];
  private _channelIds: number[];
  public live_children: number;
  public archived_children: number;

  constructor(data: EditionMapData, private _map: EditionMap) {
    this.name = data.name;
    this.id = data.id;
    this.live_children = data.live_children;
    this.archived_children = data.archived_children;
    this._programIds = data.programs.map(p => p.id);
    this._journeyIds = data.journeys.map(j => j.id);
    this._resourceIds = data.resources.map(r => r.id);
    this._sessionIds = data.sessions.map(r => r.id);
    this._channelIds = data.channels.map(r => r.id);
  }

  get programs(): ProgramForMap[] {
    return this._programIds.map(id => this._map.getProgram(id));
  }

  get journeys(): JourneyForMap[] {
    return this._journeyIds.map(id => this._map.getJourney(id));
  }

  get resources(): ResourceForMap[] {
    return this._resourceIds.map(id => this._map.getResource(id));
  }

  get sessions(): SessionForMap[] {
    return this._sessionIds.map(id => this._map.getSession(id));
  }

  get channels(): ChannelForMap[] {
    return this._channelIds.map(id => this._map.getChannel(id));
  }
}

class ProgramForMap {
  public name: string;
  public id: number;
  public live_children: number
  public archived_children: number
  private _waveIds: number[];
  private _teamIds: number[];

  constructor(data: ProgramMapData, private _map: EditionMap) {
    this.name = data.name;
    this.id = data.id;
    this.live_children = data.live_children;
    this.archived_children = data.archived_children;
    this._waveIds = data.waves.map(w => w.id);
    this._teamIds = data.teams.map(t => t.id);
  }

  get waves(): WaveForMap[] {
    return this._waveIds.map(id => this._map.getWave(id));
  }

  get teams(): TeamForMap[] {
    return this._teamIds.map(id => this._map.getTeam(id));
  }

  get edition(): EditionForMap {
    return this._map.getEdition()
  }
}

class WaveForMap {
  public name: string;
  public id: number;
  public live_children: number
  public archived_children: number
  public _sessionId: number
  private _teamIds: number[];
  private _programId: number;

  constructor(data: WaveMapData, private _map: EditionMap) {
    this.name = data.name;
    this.id = data.id;
    this.live_children = data.live_children;
    this.archived_children = data.archived_children;
    this._sessionId = data.session_id;
    this._teamIds = data.teams.map(t => t.id);
    this._programId = data.programId;
  }

  get teams(): TeamForMap[] {
    return this._teamIds.map(id => this._map.getTeam(id));
  }

  get program(): ProgramForMap {
    return this._map.getProgram(this._programId);
  }

  get session(): SessionForMap {
    return this._map.getSession(this._sessionId);
  }
}

class TeamForMap {
  public name: string;
  public id: number;
  public live_children: number
  public archived_children: number;
  private _waveId: number;
  private _programId: number;

  constructor(data: TeamMapData, private _map: EditionMap) {
    this.name = data.name;
    this.id = data.id;
    this.live_children = data.live_children;
    this.archived_children = data.archived_children;
    this._programId = data.programId;
    this._waveId = data.waveId;
  }

  get program(): ProgramForMap {
    return this._map.getProgram(this._programId);
  }

  get wave(): WaveForMap {
    return this._waveId ? this._map.getWave(this._waveId) : null;
  }
}

class JourneyForMap {
  public name: string;
  public id: number;

  constructor(data: JourneyMapData, private _map: EditionMap) {
    this.name = data.name;
    this.id = data.id;
  }
}

class ResourceForMap {
  public name: string;
  public id: number;

  constructor(data: ResourceMapData, private _map: EditionMap) {
    this.name = data.name;
    this.id = data.id;
  }
}

class SessionForMap {
  public name: string;
  public id: number;
  private programId: number;
  private waveId: number;

  constructor(data: SessionMapData, private _map: EditionMap) {
    this.name = data.name;
    this.id = data.id;
    this.programId = data.program_id;
    this.waveId = data.wave_id
  }

  get program(): ProgramForMap {
    return this._map.getProgram(this.programId);
  }

  get wave(): WaveForMap {
    return this._map.getWave(this.waveId);
  }
}

class ChannelForMap {
  public name: string;
  public id: number;

  constructor(data: ChannelMapData, private _map: EditionMap) {
    this.name = data.name;
    this.id = data.id;
  }
}

export class EditionMap {
  private teams = {};
  private waves = {};
  private programs = {};
  private journeys = {};
  private resources = {};
  private sessions = {};
  private channels = {};

  constructor(private mapData: EditionMapData) {
    mapData.programs.forEach(program => {
      this.programs[program.id] = program;
      program.waves.forEach(wave => {
        wave.programId = program.id;
        this.waves[wave.id] = wave;
        wave.teams.forEach(team => {
          team.programId = program.id;
          team.waveId = wave.id;
          this.teams[team.id] = team;
        });
      })
      program.teams.forEach(team => {
        team.programId = program.id;
        this.teams[team.id] = team;
      });
    });
    mapData.journeys.forEach(journey => {
      this.journeys[journey.id] = journey;
    });
    mapData.resources.forEach(resource => {
      this.resources[resource.id] = resource;
    });
    mapData.sessions.forEach(session => {
      this.sessions[session.id] = session;
    });
    mapData.channels.forEach(channel => {
      this.channels[channel.id] = channel;
    });
  }

  public getEdition(): EditionForMap {
    return new EditionForMap(this.mapData, this);
  }

  public getProgram(programId: number): ProgramForMap {
    return new ProgramForMap(this.programs[programId], this);
  }

  public getWave(waveId: number): WaveForMap {
    return new WaveForMap(this.waves[waveId], this);
  }

  public getTeam(teamId: number): TeamForMap {
    return new TeamForMap(this.teams[teamId], this);
  }

  public getJourney(journeyId: number): JourneyForMap {
    return new JourneyForMap(this.journeys[journeyId], this);
  }

  public getResource(resourceId: number): ResourceForMap {
    return new ResourceForMap(this.resources[resourceId], this);
  }

  public getSession(sessionId: number): SessionForMap {
    return new SessionForMap(this.sessions[sessionId], this);
  }

  public getChannel(channelId: number): ChannelForMap {
    return new ChannelForMap(this.channels[channelId], this);
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/editions/edition-map.ts